import { useRecordOrderQuoteViewMutation } from '../generated/hooks';
import { useEffect, useRef } from 'react';

/**
 * Records that a human opened this quote, once per mounted quote id.
 *
 * Fire-and-forget by design: the result says whether we captured a data point,
 * never whether the page is healthy, so nothing here surfaces to the user.
 * Shared by every app that renders a quote: admin, client portal and the guest
 * shell all call this identically, and the backend derives the channel
 * (ADMIN_PORTAL / CLIENT_PORTAL / GUEST_LINK) from the session.
 *
 * Only call it from a quote detail page — list pages, prefetch and background
 * refetches must not produce views, because this mutation is the only thing
 * that does and the counts are only as honest as its call sites. Repeat calls
 * inside 30 minutes refresh the timestamp without counting a second session,
 * so a remount is free.
 */
export function useRecordQuoteView(orderQuoteId: string | undefined) {
	const [recordView] = useRecordOrderQuoteViewMutation();
	const recordedId = useRef<string | null>(null);

	useEffect(() => {
		if (!orderQuoteId || recordedId.current === orderQuoteId) return;
		recordedId.current = orderQuoteId;
		void recordView({ variables: { orderQuoteId } }).catch(() => {});
	}, [orderQuoteId, recordView]);
}
